import { motion } from "framer-motion";
import { Phone, MapPin, Mail, Clock, ExternalLink } from "lucide-react";
import { staggerContainer, fadeUp, scaleIn } from "../animations/variants";

const MAPS_URL =
  "https://www.google.com/maps?q=18.67553977433319,78.10168465911897";

const contactItems = [
  {
    icon: Phone,
    title: "Phone",
    lines: ["+91 63011 95478", "63011 91478"],
    href: "tel:+916301195478",
    color: "bg-primary-50 text-primary-600",
    hoverColor: "hover:bg-primary-100",
    cardClass: "bg-primary-50 border-primary-200",
    titleClass: "text-primary-700",
    lineClass: "text-primary-700",
    iconWrapClass: "bg-blue-500 text-white",
  },
  {
    icon: MapPin,
    title: "Address",
    lines: [
      "Door No. 5-7-627 Oppsite of Municipal complex,",
      "Rajiv Gandhi Auditorium Road Doctor Line,",
      "khaleelwadi.",
    ],
    href: MAPS_URL,
    color: "bg-rose-50 text-rose-600",
    hoverColor: "hover:bg-rose-100",
    cardClass: "bg-rose-50 border-rose-200",
    titleClass: "text-rose-700",
    lineClass: "text-rose-700",
    iconWrapClass: "bg-rose-500 text-white",
  },
  {
    icon: Mail,
    title: "Email",
    lines: ["indurctmricenter@gmail.com"],
    href: "mailto:indurctmricenter@gmail.com",
    color: "bg-violet-50 text-violet-600",
    hoverColor: "hover:bg-violet-100",
    cardClass: "bg-violet-50 border-violet-200",
    titleClass: "text-violet-700",
    lineClass: "text-violet-700",
    iconWrapClass: "bg-violet-500 text-white",
  },
  {
    icon: Clock,
    title: "Hours",
    lines: ["Mon–Sun: 8:00 AM – 9:00 PM", "Emergency: 24/7"],
    color: "bg-emerald-50 text-emerald-600",
    hoverColor: "hover:bg-emerald-100",
    cardClass: "bg-emerald-50 border-emerald-200",
    titleClass: "text-emerald-700",
    lineClass: "text-emerald-700",
    iconWrapClass: "bg-emerald-500 text-white",
  },
];

export default function ContactSection() {
  return (
    <section
      id="contact"
      className="pt-14 pb-24"
      style={{ background: "#87CEEB" }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Heading */}
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          className="text-center mb-16"
        >
          <motion.p variants={fadeUp} className="section-subtitle text-black">
            Find Us
          </motion.p>
          <motion.h2
            variants={fadeUp}
            className="section-title text-gray-900 mb-5"
          >
            Get in <span className="gradient-text">Touch</span>
          </motion.h2>
          <motion.p
            variants={fadeUp}
            className="text-gray-500 max-w-xl mx-auto text-lg"
          >
            Door No. 5-7-627 Oppsite of municipal complex, Rajiv Gandhi
            Auditorium Road Doctor Line, khaleelwadi.
          </motion.p>
        </motion.div>

        {/* Contact cards */}
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-60px" }}
          className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-14"
        >
          {contactItems.map(
            ({
              icon: Icon,
              title,
              lines,
              href,
              color,
              hoverColor,
              cardClass,
              titleClass,
              lineClass,
              iconWrapClass,
            }) => (
              <motion.div
                key={title}
                variants={scaleIn}
                whileHover={{ y: -6, scale: 1.02 }}
                transition={{ duration: 0.3 }}
                className={`rounded-2xl p-6 text-center hover:shadow-lg transition-all duration-300 group border ${cardClass ?? "bg-gray-50 border-gray-100"}`}
              >
                <div
                  className={`w-14 h-14 rounded-2xl ${iconWrapClass ?? color} flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300`}
                >
                  <Icon className="w-6 h-6" />
                </div>
                <h3
                  className={`font-poppins font-semibold mb-2 ${titleClass ?? "text-gray-800"}`}
                >
                  {title}
                </h3>
                {lines.map((line) => (
                  <p
                    key={line}
                    className={`text-sm ${lineClass ?? "text-gray-500"}`}
                  >
                    {line}
                  </p>
                ))}
                {href && (
                  <a
                    href={href}
                    target={href.startsWith("http") ? "_blank" : undefined}
                    rel="noopener noreferrer"
                    className={`inline-flex items-center gap-1.5 mt-3 text-xs font-semibold px-3 py-1.5 rounded-full ${color} ${hoverColor} transition-colors`}
                  >
                    {href.startsWith("http") ? (
                      <>
                        <ExternalLink className="w-3 h-3" /> View Map
                      </>
                    ) : (
                      "Contact Now"
                    )}
                  </a>
                )}
              </motion.div>
            ),
          )}
        </motion.div>

        {/* Google Maps Embed */}
        <motion.div
          variants={scaleIn}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="rounded-3xl overflow-hidden shadow-xl border border-gray-100 relative"
        >
          <iframe
            title="Indur CT & MRI Centre Location"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d476.8!2d78.10145886162627!3d18.67567448820044!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTjCsDQwJzMyLjQiTiA3OMKwMDYnMDUuMiJF!5e0!3m2!1sen!2sin!4v1"
            width="100%"
            height="400"
            style={{ border: 0, display: "block" }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
          {/* Overlay button */}
          <a
            href={MAPS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="absolute bottom-4 right-4 inline-flex items-center gap-1.5 bg-white text-primary-700 text-sm font-semibold px-4 py-2.5 rounded-full shadow-lg hover:bg-primary-50 transition-colors border border-primary-100"
          >
            <ExternalLink className="w-3.5 h-3.5" /> Open in Maps
          </a>
        </motion.div>

        {/* Bottom CTA Strip */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mt-12 rounded-3xl bg-gradient-to-r from-primary-600 via-primary-500 to-sky-500 p-8 flex flex-col sm:flex-row items-center justify-between gap-5 text-white"
        >
          <div>
            <h3 className="font-poppins font-bold text-xl mb-1">
              Need urgent imaging?
            </h3>
            <p className="text-white/75 text-sm">
              Walk in any time — our emergency CT & MRI is available 24/7.
            </p>
          </div>
          <div className="flex flex-wrap gap-3 shrink-0">
            <a
              href="tel:+916301195478"
              className="inline-flex items-center gap-2 bg-white text-primary-700 font-bold px-6 py-3 rounded-full hover:bg-sky-50 transition-colors shadow-lg"
            >
              <Phone className="w-4 h-4" /> +91 63011 95478
            </a>
            <a
              href="tel:6301191478"
              className="inline-flex items-center gap-2 border-2 border-white/40 text-white font-semibold px-6 py-3 rounded-full hover:bg-white/10 transition-colors"
            >
              <Phone className="w-4 h-4" /> 63011 91478
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
